import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black pt-20">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `linear-gradient(to right, rgb(59, 130, 246) 1px, transparent 1px), linear-gradient(to bottom, rgb(59, 130, 246) 1px, transparent 1px)`,
            backgroundSize: '80px 80px'
          }}
        />
      </div>

      {/* Glass Morphism Floating Cards */}
      <motion.div
        className="absolute top-1/4 left-1/4 z-10"
        animate={{ 
          y: [0, -30, 0],
          rotateY: [0, 15, 0],
          scale: [1, 1.1, 1]
        }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformStyle: "preserve-3d" }}
      >
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-2xl">
          <div className="text-blue-400 text-6xl font-bold">4hrs</div>
          <div className="text-white/60 mt-2">Manual</div>
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-1/4 right-1/4 z-10"
        animate={{ 
          y: [0, 30, 0],
          rotateY: [0, -15, 0],
          scale: [1, 1.15, 1]
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        style={{ transformStyle: "preserve-3d" }}
      >
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-2xl">
          <div className="text-blue-400 text-6xl font-bold">6min</div>
          <div className="text-white/60 mt-2">SupaAid</div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="relative z-20 text-center px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* Small Badge */}
          <motion.div
            className="inline-block mb-8"
            whileHover={{ scale: 1.1 }}
            animate={{ y: [0, -5, 0] }}
            transition={{ y: { duration: 2, repeat: Infinity } }}
          >
            <div className="bg-blue-500/10 backdrop-blur-md border border-blue-500/20 rounded-full px-6 py-3">
              <span className="text-blue-400 font-semibold">AI-Powered Emergency Response</span>
            </div>
          </motion.div>

          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-9xl font-bold text-white mb-6 sm:mb-8 leading-tight px-2">
            SupaAid
          </h1>
          
          <motion.h2 
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white/90 font-medium mb-4 sm:mb-6 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Saving Lives, One Ping at a Time
          </motion.h2>
          
          <motion.p 
            className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/60 max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Cut emergency response time from 4 hours to 6 minutes. AI-powered medical coordination + 24/7 citizen triage.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-6 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <motion.div
              whileHover={{ scale: 1.05, z: 50 }}
              whileTap={{ scale: 0.95 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <Button 
                size="lg" 
                className="bg-gray-500 hover:bg-gray-600 text-white font-bold text-lg px-10 py-7 rounded-2xl shadow-2xl border-2 border-gray-400 flex items-center gap-3 group cursor-not-allowed opacity-75"
                disabled
              >
                Coming Soon
                <ArrowRight className="w-5 h-5 opacity-50" />
              </Button>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05, z: 50 }}
              whileTap={{ scale: 0.95 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <Button 
                size="lg" 
                variant="outline"
                className="bg-white/5 backdrop-blur-md hover:bg-white/10 text-white/60 font-bold text-lg px-10 py-7 rounded-2xl shadow-2xl border-2 border-white/20 cursor-not-allowed opacity-75"
                disabled
              >
                Coming Soon
              </Button>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="mt-12 sm:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-3xl mx-auto px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            {[
              { value: '300K+', label: 'Lives at Risk' },
              { value: '6 min', label: 'Response Time' },
              { value: '24/7', label: 'AI Support' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6"
                whileHover={{ 
                  scale: 1.1, 
                  rotateY: 10,
                  z: 50
                }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ transformStyle: "preserve-3d" }}
              >
                <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-2">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <motion.div 
            className="w-1 h-2 bg-white/60 rounded-full"
            animate={{ y: [0, 12, 0], opacity: [1, 0, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
